import { z } from 'zod';
import { Language } from '../enums.js';
import { E164, TimestampSchema } from '../primitives.js';
import { SmsProvider } from '../contracts/sms.js';
/**
 * `businesses/{businessId}/notifications/{notificationId}` — the ledger
 * (doc 21 D7).
 *
 * The document id is DETERMINISTIC (`{appointmentId}_{kind}`, reminders
 * `{appointmentId}_reminder_{offsetMin}`) and creating it IS the claim:
 * Firestore triggers are at-least-once, and a duplicate here is a real SMS to
 * a real customer, not a wasted log line.
 *
 * It earns its keep three more times over — delivery status (R11), the GDPR
 * audit record of what we sent to whom (R13), and the portal's failure
 * surface. Server-written entirely; owners read, never write.
 */
export const SmsNotificationKind = z.enum(['booked', 'moved', 'cancelled', 'reminder']);
/**
 * Why nothing was sent. A suppressed row is a FEATURE, not an absence: "we
 * deliberately did not send, and here is why" answers most support questions
 * about this feature, and a missing document cannot answer any of them.
 */
export const SmsSuppressReason = z.enum([
    /** The appointment has no customer phone — withheld caller ID. */
    'no_phone',
    /** The business has this notification kind switched off. */
    'disabled',
    /** Consent missing or revoked (doc 21 §5). */
    'consent',
    /** A reminder that could only land inside quiet hours. */
    'quiet_hours',
    /** The business's monthly cap is spent. */
    'cap_reached',
    /** A reminder whose appointment is already closer than the offset. */
    'too_late',
    /** Business-owner test call (doc 13a §6) — never messages a real client. */
    'test_appointment',
    /** No provider serves this destination. */
    'no_provider',
    /** The caller undid it before hanging up — booked and cancelled, or replaced,
     * inside one conversation (doc 22 D4). The client was never told anything, so
     * there is nothing to correct; this row exists so that "I never got a
     * confirmation" has an answer. */
    'superseded_in_call',
]);
export const SmsNotificationStatus = z.enum(['claimed', 'sent', 'delivered', 'failed', 'suppressed']);
export const SmsNotificationSchema = z.object({
    kind: SmsNotificationKind,
    appointmentId: z.string().min(1),
    /** Null on a `no_phone` suppression — there was nobody to address. */
    to: E164.nullable().default(null),
    language: Language,
    /** Null until a provider is resolved (i.e. on every suppression). */
    provider: SmsProvider.nullable().default(null),
    senderId: z.string().default(''),
    /**
     * NOT WRITTEN any more (doc 21 D7a). Templates are fixed in code and
     * versioned in git, so the exact text is reproducible from `kind` plus the
     * appointment — storing a copy duplicated the customer's name and appointment
     * time into a second place and answered nothing the row could not.
     *
     * The field stays so rows written before that decision keep parsing.
     */
    body: z.string().default(''),
    segments: z.number().int().min(0).default(0),
    encoding: z.enum(['gsm7', 'ucs2']).default('gsm7'),
    status: SmsNotificationStatus,
    suppressReason: SmsSuppressReason.nullable().default(null),
    /** The vendor's own id, for correlating delivery reports and support cases. */
    providerMessageId: z.string().nullable().default(null),
    error: z.string().max(500).nullable().default(null),
    /** Reminder rows only: which configured offset this row satisfies. */
    offsetMin: z.number().int().nullable().default(null),
    claimedAt: TimestampSchema,
    sentAt: TimestampSchema.optional(),
    deliveredAt: TimestampSchema.optional(),
});
export const SmsNotificationWithIdSchema = SmsNotificationSchema.extend({ id: z.string() });
/** The ledger document id — deterministic, and therefore the claim (D7). */
export function notificationId(appointmentId, kind, offsetMin) {
    return kind === 'reminder' ? `${appointmentId}_reminder_${offsetMin ?? 0}` : `${appointmentId}_${kind}`;
}
//# sourceMappingURL=notification.js.map